import React, { useState } from 'react';
import { submitRating } from '../services/ratingsApi';

const StarButton = ({ filled, hovered, onClick, onMouseEnter, onMouseLeave, value }) => (
  <button
    type="button"
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px 2px',
      fontSize: 32,
      lineHeight: 1,
      transition: 'transform .1s',
      transform: (filled || hovered) ? 'scale(1.15)' : 'scale(1)',
      color: (filled || hovered) ? '#f59e0b' : '#d1d5db',
      filter: (filled || hovered) ? 'drop-shadow(0 1px 4px #f59e0b55)' : 'none',
    }}
    aria-label={`${value} star`}
  >
    ★
  </button>
);

const LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

const RatingModal = ({ appointment, onClose, onSubmitted }) => {
  const [rating, setRating]       = useState(0);
  const [hovered, setHovered]     = useState(0);
  const [comment, setComment]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState(null);

  const displayRating = hovered || rating;

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await submitRating({
        appointmentId: appointment.id,
        rating,
        comment,
      });
      onSubmitted?.(result);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not submit rating. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15,10,40,0.55)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px 16px', animation: 'fadeUp .2s ease both',
      }}
      onClick={e => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div style={{
        background: '#fff', borderRadius: 20, width: '100%', maxWidth: 420,
        padding: '32px 28px',
        boxShadow: '0 8px 40px rgba(79,70,229,0.18), 0 2px 8px rgba(0,0,0,0.08)',
        animation: 'dropIn .22s ease both',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>⭐</div>
          <div style={{
            fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: 18,
            color: '#1e1b4b', marginBottom: 4,
          }}>
            Rate your consultation
          </div>
          <div style={{ fontSize: 13.5, color: '#6b7280', fontFamily: 'DM Sans, sans-serif' }}>
            with <strong style={{ color: '#1e1b4b' }}>{appointment?.doctorName || 'your doctor'}</strong>
          </div>
        </div>

        {/* Stars */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4, margin: '20px 0 6px' }}>
          {[1, 2, 3, 4, 5].map(v => (
            <StarButton
              key={v}
              value={v}
              filled={v <= rating}
              hovered={v <= hovered && v > rating}
              onClick={() => setRating(v)}
              onMouseEnter={() => setHovered(v)}
              onMouseLeave={() => setHovered(0)}
            />
          ))}
        </div>

        {/* Label */}
        <div style={{
          textAlign: 'center', height: 20, marginBottom: 16,
          fontSize: 13, fontWeight: 600, color: '#f59e0b',
          fontFamily: 'DM Sans, sans-serif',
          transition: 'opacity .15s',
          opacity: displayRating ? 1 : 0,
        }}>
          {LABELS[displayRating]}
        </div>

        {/* Comment */}
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Share your experience (optional)"
          rows={3}
          maxLength={500}
          style={{
            width: '100%', boxSizing: 'border-box',
            border: '1.5px solid #e5e7eb', borderRadius: 12,
            padding: '10px 14px', fontSize: 13.5, color: '#374151',
            fontFamily: 'DM Sans, sans-serif', resize: 'none',
            outline: 'none', transition: 'border-color .15s',
          }}
          onFocus={e => e.target.style.borderColor = '#818cf8'}
          onBlur={e => e.target.style.borderColor = '#e5e7eb'}
        />
        <div style={{ textAlign: 'right', fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
          {comment.length}/500
        </div>

        {error && (
          <div style={{
            marginTop: 8, padding: '8px 12px', borderRadius: 10,
            background: '#fef2f2', color: '#dc2626', fontSize: 12.5,
            fontFamily: 'DM Sans, sans-serif',
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              flex: 1, padding: '11px 0', borderRadius: 12,
              border: '1.5px solid #e5e7eb', background: '#fff',
              color: '#374151', fontSize: 14, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
            }}
          >
            Skip
          </button>
          <button
            onClick={handleSubmit}
            disabled={!rating || submitting}
            style={{
              flex: 2, padding: '11px 0', borderRadius: 12, border: 'none',
              background: rating && !submitting
                ? 'linear-gradient(135deg, #4f46e5, #7c3aed)'
                : '#e5e7eb',
              color: rating && !submitting ? '#fff' : '#9ca3af',
              fontSize: 14, fontWeight: 700,
              cursor: rating && !submitting ? 'pointer' : 'not-allowed',
              fontFamily: 'DM Sans, sans-serif',
              transition: 'all .15s',
            }}
          >
            {submitting ? 'Submitting…' : 'Submit Rating'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RatingModal;